import {
  Alert,
  Button,
  FileUpload,
  Hint,
  HintBody,
  Modal,
  ModalVariant,
  Stack,
  StackItem,
} from '@patternfly/react-core'
import React, { useState } from 'react'
import { DowntimeAPI, Site } from '../../client'
import { ActionState, requestStateMessages } from '../../components/actionState'
import { queryClient } from '../../services/api'
import { CACHE_KEY_ONS } from './constants'

const SAVE_BUTTON_LABEL = {
  idle: 'Salvar',
  pending: 'Salvando',
  failure: 'Não Salvo',
  success: 'Salvo',
}

const ModalUploadONSSheet = ({
  isOpen,
  setOpen,
  site,
}: {
  isOpen: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  site: Site
}) => {
  const [planilhaFile, setPlanilhaFile] = useState<File | null>(null)
  const [statusResponse, setStatusResponse] = useState<string>('')
  const [actionState, setActionState] = useState<ActionState>('idle')
  const [isFileLoading, setIsFileLoading] = useState(false)
  const [successMessages, setSuccessMessages] = useState<string[]>([])
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

  const handleFileRejected = (_rejectedFiles: any, _event: any) => {
    setPlanilhaFile(null)
  }
  const handleFileReadStarted = (_fileHandle: File): void => {
    setIsFileLoading(true)
  }
  const handleFileReadFinished = (_fileHandle: File): void => {
    setIsFileLoading(false)
  }
  const handleFileInputChange = (_event: any, file: File) => {
    setPlanilhaFile(file)
  }
  const handleClear = (_event: any) => {
    setPlanilhaFile(null)
    setStatusResponse('')
    setActionState('idle')
    setIsFileLoading(false)
  }
  const handleCloseModal = () => {
    setOpen(false)
    setPlanilhaFile(null)
    setStatusResponse('')
    setActionState('idle')
    setIsFileLoading(false)
    setSuccessMessages([])
  }
  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false)
    setSuccessMessages([])
    setActionState('idle')
  }

  const handleSubmit = async () => {
    setStatusResponse('')
    if (!planilhaFile) {
      return
    }

    setActionState('pending')

    try {
      const response = await DowntimeAPI.workbookUpload({
        siteId: site.site_id,
        formData: { xlsx: planilhaFile },
      })
      setActionState('idle')
      if (response?.message && Array.isArray(response.message)) {
        setSuccessMessages(response.message)
      }
      await queryClient.invalidateQueries([CACHE_KEY_ONS, site.site_id])
      setOpen(false)
      setPlanilhaFile(null)
      setStatusResponse('')
      setIsFileLoading(false)
      setTimeout(() => {
        setIsSuccessModalOpen(true)
      }, 100)
    } catch (error: any) {
      setStatusResponse(error?.body?.detail ?? 'Unable to find error message')
      setActionState('failure')
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        variant={ModalVariant.medium}
        title={`${site.site_name.toUpperCase()} Cadastrar Restrições`}
        actions={[
          <Button
            id='salvar-button'
            key='create'
            variant='primary'
            onClick={handleSubmit}
            isDisabled={actionState === 'pending'}
            isLoading={actionState === 'pending'}
          >
            {SAVE_BUTTON_LABEL[actionState]}
          </Button>,
          <Button key='cancel' variant='secondary' isDanger onClick={handleCloseModal}>
            Cancelar
          </Button>,
        ]}
      >
        <Stack hasGutter>
          {actionState === 'failure' ? (
            <>
              <StackItem>{requestStateMessages()[actionState]}</StackItem>
              <StackItem>
                <Hint>
                  <HintBody>{statusResponse}</HintBody>
                </Hint>
              </StackItem>
            </>
          ) : (
            <StackItem>
              <Hint>
                <HintBody>
                  Importação das restrições da ONS de {site.site_name.toLocaleUpperCase()}
                </HintBody>
              </Hint>
            </StackItem>
          )}
          <StackItem>
            <FileUpload
              id='paradas-ons-file-upload'
              filename={planilhaFile?.name}
              filenamePlaceholder='Arraste e solte ou carregue um a planilha aqui'
              onFileInputChange={handleFileInputChange}
              onReadFinished={(_event, _fileHandle: File) => handleFileReadFinished(_fileHandle)}
              onReadStarted={(_event, _fileHandle: File) => handleFileReadStarted(_fileHandle)}
              onClearClick={handleClear}
              hideDefaultPreview
              browseButtonText='Upload'
              isLoading={isFileLoading}
              dropzoneProps={{
                accept: {
                  'application/excel': ['.xlsx', '.csv', '.xls'],
                },
                onDropRejected: handleFileRejected,
              }}
            />
          </StackItem>
        </Stack>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        variant={ModalVariant.small}
        title={`${site.site_name.toUpperCase()}`}
        actions={[
          <Button key='close' variant='primary' onClick={handleCloseSuccessModal}>
            OK
          </Button>,
        ]}
      >
        <Stack hasGutter>
          <StackItem>
            <Alert variant='success' title='Importação realizada com sucesso' isInline />
          </StackItem>
          {successMessages.length > 0 && (
            <StackItem>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.125rem',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {successMessages.map((message, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.125rem 0.5rem',
                      backgroundColor: 'var(--pf-v5-global--BackgroundColor--200)',
                      borderRadius: 'var(--pf-v5-global--BorderRadius--sm)',
                      fontSize: 'var(--pf-v5-global--FontSize--sm)',
                      lineHeight: '1.25',
                    }}
                  >
                    {message}
                  </div>
                ))}
              </div>
            </StackItem>
          )}
        </Stack>
      </Modal>
    </>
  )
}

export default ModalUploadONSSheet
